import React, { createContext, useContext, useState } from 'react';
import { en } from './en';
import { ru } from './ru';
import { de } from './de';
import { cs } from './cs';
import { es } from './es';
import { ja } from './ja';
import { fr } from './fr';
import { pt } from './pt';
import { enGb } from './en-gb';
import { STORAGE_KEYS } from '../utils/constants';

export type Language = 'en' | 'en-GB' | 'ru' | 'de' | 'cs' | 'es' | 'ja' | 'fr' | 'pt';
type Translations = typeof en;

interface LocalizationContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const translations: Record<Language, Translations> = {
  en, 'en-GB': enGb,
  ru, de, cs, es,
  ja, fr, pt,
};

const validLanguages: Language[] = ['en', 'en-GB', 'ru', 'de', 'cs', 'es', 'ja', 'fr', 'pt'];

export const LocalizationContext = createContext<LocalizationContextType | null>(null);

export const LocalizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    let saved = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
    // Migrate from old key name
    if (!saved) {
      saved = localStorage.getItem('app-language');
      if (saved) {
        localStorage.setItem(STORAGE_KEYS.LANGUAGE, saved);
        localStorage.removeItem('app-language');
      }
    }
    return validLanguages.includes(saved as Language) ? saved as Language : 'en';
  });

  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage);
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, newLanguage);
  };

  const value = {
    language,
    setLanguage: handleLanguageChange,
    t: translations[language]
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
};
